// Angular
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { IsActiveMatchOptions, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

// RxJS
import { filter, fromEvent, map, shareReplay } from 'rxjs';

// UI Components
import {
  SidebarMenuGroupComponent,
  SidebarMenuItemComponent,
  SidebarSpacerComponent,
  SidebarUserComponent,
  SidebarComponent,
  ThemeMode,
  ButtonComponent,
  DividerComponent,
} from '@sms-ir/ui-components';

// App constants
import { STATIC_SIDEBAR_MENU } from '@app/core/constants';

// App services
import { LayoutService, SessionCleanupService, TokenService } from '@app/core/services';
import { ThemeService } from '@app/core/services/theme.service';
import { SidebarMenuService } from '@app/core/services/sidebar-menu.service';
import { BackdropService } from '@app/core/services/backdrop.service';
import { RegistrationFacade } from '@app/features/registration/services/registration.facade';
import { SignalRFacadeService } from '@app/core/services/signalr-facade.service';

const EXACT_SIDEBAR_MATCH: IsActiveMatchOptions = {
  paths: 'exact',
  queryParams: 'ignored',
  fragment: 'ignored',
  matrixParams: 'ignored',
};

@Component({
  selector: 'app-main-layout',
  standalone: true,
  templateUrl: './main-layout.component.html',
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    SidebarComponent,
    SidebarSpacerComponent,
    SidebarMenuItemComponent,
    SidebarUserComponent,
    SidebarMenuGroupComponent,
    ButtonComponent,
    DividerComponent,
  ],
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit {
  private document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly sessionCleanup = inject(SessionCleanupService);
  private readonly backdrop = inject(BackdropService);
  protected layoutService = inject(LayoutService);
  private menuService = inject(SidebarMenuService);
  private themeService = inject(ThemeService);
  private readonly registrationFacade = inject(RegistrationFacade);
  private readonly signalRFacade = inject(SignalRFacadeService);

  readonly exactSidebarMatch = EXACT_SIDEBAR_MATCH;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly showRegistrationExitButton = computed(() =>
    this.currentUrl().includes('/registration')
  );


  isSidebarOpen = signal(false);

  visibleGroups = this.menuService.visibleGroups;

  theme = computed(() => {
    return this.themeService.mode()
  });

  isScrolled = toSignal(
    fromEvent(this.document, 'scroll').pipe(
      map(() => {
        const scrollTop = this.document.documentElement.scrollTop || this.document.body.scrollTop;
        return scrollTop > 20;
      }),
      shareReplay(1)
    ),
    { initialValue: false }
  );


  ngOnInit() {
    this.menuService.setStatic(STATIC_SIDEBAR_MENU);
    void this.signalRFacade.connect();
  }

  onSidebarToggle(value: boolean): void {
    this.isSidebarOpen.set(value);
    if (value) {
      this.backdrop.open({
        source: 'sidebar',
        hideOnDesktop: true,
        closeOnClick: true,
        onClick: () => {
          this.onSidebarToggle(false);
        },
      });
    } else {
      this.backdrop.close('sidebar');
    }
  }

  onThemeToggle(value: ThemeMode): void {
    this.themeService.setTheme(value);
  }

  onLogout(): void {
    this.onSidebarToggle(false);
    this.sessionCleanup.clearCachedUserData();
    this.tokenService.removeTokens();
    void this.router.navigate(['/auth/identify']);
  }

  onRegistrationSaveAndExit(): void {
    this.registrationFacade.saveAndExit();
  }
}
